import React, { useMemo, useState } from "react"

import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ukToday } from "@/constants/sheriffOptions"

const OLDEST_MONTH = "2025-06"

const QUARTER_MONTHS = ["Jan – Mar", "Apr – Jun", "Jul – Sep", "Oct – Dec"]

const quarterIndex = (date: string) => {
  const [year, month] = date.split("-").map(Number)
  return year * 4 + Math.floor((month - 1) / 3)
}

const recentQuarters = () => {
  const current = quarterIndex(ukToday())
  const oldest = quarterIndex(OLDEST_MONTH)
  return Array.from({ length: current - oldest + 1 }, (_, i) => {
    const q = (current - i) % 4
    const y = Math.floor((current - i) / 4)
    return {
      value: `${y}-Q${q + 1}`,
      label: `Q${q + 1} ${y} (${QUARTER_MONTHS[q]})`,
    }
  })
}

interface ExportApplicationsButtonProps {
  onExport: (quarter: string) => Promise<void>
}

const ExportApplicationsButton: React.FC<ExportApplicationsButtonProps> = ({ onExport }) => {
  const quarters = useMemo(recentQuarters, [])
  const [open, setOpen] = useState(false)
  const [quarter, setQuarter] = useState(quarters[0].value)
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await onExport(quarter)
      setOpen(false)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 gap-2 rounded-full border-palette-jungleGreen200 px-5"
        >
          <Icon name="downloadSimple" className="h-4 w-4 text-palette-jungleGreen700" />
          Export
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="end">
        <h4 className="font-semibold text-palette-jungleGreen900 mb-1">Export applications</h4>
        <p className="text-sm text-palette-jungleGreen600 mb-3">
          Applications submitted in the selected quarter, with the current filters and search.
        </p>
        <Select value={quarter} onValueChange={setQuarter}>
          <SelectTrigger className="mb-3">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {quarters.map((q) => (
              <SelectItem key={q.value} value={q.value}>
                {q.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button className="w-full" onClick={handleExport} disabled={isExporting}>
          {isExporting ? "Exporting…" : "Export"}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

export default ExportApplicationsButton
